import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import useUploadStore from '../../stores/uploadStore';

const GlobalDropZone = ({ children }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragCounter, setDragCounter] = useState(0);
  const navigate = useNavigate();
  const { setDroppedImages, openCreateProductModal } = useUploadStore();

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Check if dragging files (not text or other elements)
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      const hasFiles = Array.from(e.dataTransfer.items).some(
        item => item.kind === 'file'
      );
      
      if (hasFiles) {
        setDragCounter(prev => prev + 1);
        setIsDragging(true);
      }
    }
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    setDragCounter(prev => {
      const newCounter = prev - 1;
      if (newCounter === 0) {
        setIsDragging(false);
      }
      return newCounter;
    });
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const validateImageFile = (file) => {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      return false;
    }
    // Max size 10MB
    if (file.size > 10 * 1024 * 1024) {
      return false;
    }
    return true;
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    setIsDragging(false);
    setDragCounter(0);
    
    const files = [...e.dataTransfer.files];
    const imageFiles = files.filter(validateImageFile);
    
    if (imageFiles.length > 0) {
      // Store images in the global store
      setDroppedImages(imageFiles);
      // Open the create product modal
      openCreateProductModal();
    }
  }, [setDroppedImages, openCreateProductModal]);

  useEffect(() => {
    // Add event listeners to the document
    document.addEventListener('dragenter', handleDragEnter);
    document.addEventListener('dragleave', handleDragLeave);
    document.addEventListener('dragover', handleDragOver);
    document.addEventListener('drop', handleDrop);
    
    return () => {
      // Cleanup
      document.removeEventListener('dragenter', handleDragEnter);
      document.removeEventListener('dragleave', handleDragLeave);
      document.removeEventListener('dragover', handleDragOver);
      document.removeEventListener('drop', handleDrop);
    };
  }, [handleDragEnter, handleDragLeave, handleDragOver, handleDrop]);

  return (
    <>
      {children}
      
      <AnimatePresence>
        {isDragging && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9999,
              pointerEvents: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background: 'rgba(33, 150, 243, 0.85)',
                backdropFilter: 'blur(4px)',
              }}
            />

            <motion.div
              initial={{ scale: 0.8, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <div
                style={{
                  position: 'relative',
                  padding: '3rem',
                  borderRadius: '1rem',
                  backgroundColor: '#fff',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                  border: '3px dashed rgba(255,255,255,0.5)',
                  textAlign: 'center',
                  minWidth: 400,
                }}
              >
                <motion.div
                  animate={{ y: [0, -10, 0] }}
                  transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
                  style={{ marginBottom: '1rem' }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 72, color: 'var(--primary, #1d4ed8)' }}>cloud_upload</span>
                </motion.div>

                <p style={{ marginBottom: '0.5rem', fontWeight: 500, fontSize: '1.25rem', color: '#1a1a1a' }}>
                  Suelta para Publicar
                </p>
                <p style={{ color: '#666', fontSize: '0.875rem' }}>
                  Arrastra tus imágenes aquí para crear un producto
                </p>

                <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'center', gap: '0.5rem' }}>
                  {[...Array(3)].map((_, i) => (
                    <motion.div
                      key={i}
                      animate={{ opacity: [0.3, 1, 0.3], scale: [0.8, 1, 0.8] }}
                      transition={{ repeat: Infinity, duration: 1.5, delay: i * 0.2, ease: 'easeInOut' }}
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: 32, color: 'var(--secondary, #7c3aed)' }}>image</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default GlobalDropZone;